import { wrapCreateBrowserRouterV7 } from '@sentry/react';
import { lazy, Suspense, type ElementType, type ReactElement, type ReactNode } from 'react';
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router';

import AppLayout from './AppLayout';
import SpinnerLoader from './components/Loaders/SpinnerLoader';
import { ProtectedRoute } from './components/ProtectedRoute';
import {
  organizationLoader,
  userLoader,
  businessUnitLoader,
  projectLoader,
  productLoader,
  contactLoader,
  osProjectLoader,
  caasClusterLoader,
  getDashboardLoader,
} from './loaders/routeLoaders';
import Notifications from './pages/Account/Notifications';
import Page404 from './pages/Error/Page404';

const AboutUs = lazy(() => import('./pages/AboutUs'));
const Account = lazy(() => import('./pages/Account'));
const AccountOverview = lazy(() => import('./pages/Account/AccountOverview'));
const Security = lazy(() => import('./pages/Account/Security'));
const ContactSecurity = lazy(() => import('./pages/ContactSecurity'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const DataPrivacy = lazy(() => import('./pages/DataPrivacy'));
const Documentation = lazy(() => import('./pages/Documentation'));
const FallBackError = lazy(() => import('./pages/Error/FallBackError'));
const GiveUsFeedback = lazy(() => import('./pages/GiveUsFeedback'));
const GovernanceTabs = lazy(() => import('./pages/Governance'));
const Gtac = lazy(() => import('./pages/GTAC'));
const Home = lazy(() => import('./pages/Home/Home'));
const OrganizationDetails = lazy(() => import('./pages/OrganizationDetails'));
const BusinessUnits = lazy(() => import('./pages/OrganizationDetails/BusinessUnits'));
const BusinessUnitDetails = lazy(
  () => import('./pages/OrganizationDetails/BusinessUnits/BusinessUnitDetails')
);
const Overview = lazy(() => import('./pages/OrganizationDetails/Overview'));
const Projects = lazy(() => import('./pages/OrganizationDetails/Projects'));
const Project = lazy(() => import('./pages/OrganizationDetails/Projects/Project'));
const Users = lazy(() => import('./pages/OrganizationDetails/Users'));
const UserDetails = lazy(() => import('./pages/OrganizationDetails/Users/UserDetails'));
const PasswordReset = lazy(() => import('./pages/PasswordReset/PasswordReset'));
const UserPasswordUpdate = lazy(() => import('./pages/PasswordReset/UserPasswordUpdate'));
const BareMetalSpecification = lazy(() => import('./pages/Products/BareMetal/Specification'));
const CaasSpecification = lazy(() => import('./pages/Products/Caas/Specification'));
const ComputeOrder = lazy(() => import('./pages/Products/Compute/ComputeOrder'));
const Evaluation = lazy(() => import('./pages/Products/Compute/Evaluation'));
const OsProject = lazy(() => import('./pages/Products/Compute/OsProject'));
const ComputeSpecification = lazy(() => import('./pages/Products/Compute/Specification'));
const MonitoringSpecification = lazy(() => import('./pages/Products/Monitoring/Specification'));
const SafescarfSpecification = lazy(() => import('./pages/Products/Safescarf/Specification'));
const Monitoring = lazy(() => import('./pages/Products/UserProducts/Monitoring'));
const Dashboards = lazy(() => import('./pages/Products/UserProducts/Monitoring/Dashboards'));
const MonitoringDashboard = lazy(
  () => import('./pages/Products/UserProducts/Monitoring/MonitoringDashboard')
);
const PanelDetails = lazy(() => import('./pages/Products/UserProducts/Monitoring/PanelDetails'));
const ProductDetails = lazy(() => import('./pages/Products/UserProducts/ProductDetails'));
const CaasClusterDetails = lazy(
  () => import('./pages/Products/UserProducts/ProductDetails/Caas/CaasClusterDetails')
);
const CaasClusters = lazy(
  () => import('./pages/Products/UserProducts/ProductDetails/Caas/CaasClusters')
);
const UserProducts = lazy(() => import('./pages/Products/UserProducts/ProductDetails/Compute'));
const SafescarfProduct = lazy(() => import('./pages/Products/UserProducts/Safescarf'));
const SafescarfMachineAccounts = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/MachineAccounts')
);
const SafescarfOverview = lazy(() => import('./pages/Products/UserProducts/Safescarf/Overview'));
const SafescarfProducts = lazy(() => import('./pages/Products/UserProducts/Safescarf/Products'));
const SafescarfProductDetails = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/Products/ProductDetails')
);
const ProductMemberDetails = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/Products/ProductMemberDetails')
);
const SafescarfProductTypes = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/ProductTypes')
);
const SafescarfProductTypeDetails = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/ProductTypes/ProductTypeDetails')
);
const ProductTypeMemberDetails = lazy(
  () => import('./pages/Products/UserProducts/Safescarf/ProductTypes/ProductTypeMemberDetails')
);
const SafescarfUsers = lazy(() => import('./pages/Products/UserProducts/Safescarf/Users'));
const Reports = lazy(() => import('./pages/Reports'));
const ContactDetails = lazy(() => import('./pages/Reports/ContactDetails'));
const HypervisorDetails = lazy(() => import('./pages/Reports/HypervisorDetails'));
const HypervisorReport = lazy(() => import('./pages/Reports/HypervisorReport'));
const JuiceGroups = lazy(() => import('./pages/Reports/JuiceGroups'));
const TenantDetails = lazy(() => import('./pages/Reports/TenantDetails'));
const TenantsReport = lazy(() => import('./pages/Reports/TenantsReport'));
const UserActivation = lazy(() => import('./pages/UserActivation'));
const WhatsNew = lazy(() => import('./pages/WhatsNew'));

const lazyRoute = (element: ReactNode): ReactElement => (
  <Suspense
    fallback={
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <SpinnerLoader
          isLoading={true}
          labelText='LOADING'
          labelAlignment='horizontal'
        />
      </div>
    }
  >
    {element}
  </Suspense>
);

const protectedLazyRoute = (permission: string, component: ElementType): ReactElement =>
  lazyRoute(
    <ProtectedRoute
      permission={permission}
      component={component}
    />
  );

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: lazyRoute(<FallBackError />),
    children: [
      { index: true, element: lazyRoute(<Home />) },
      { path: 'monitoring-dashboards', element: lazyRoute(<Dashboards />) },
      {
        path: 'monitoring-dashboards/:dashboardId',
        element: lazyRoute(<Monitoring />),
        loader: getDashboardLoader,
        children: [
          { index: true, element: lazyRoute(<MonitoringDashboard />) },
          { path: ':panelId', element: lazyRoute(<PanelDetails />) },
        ],
      },
      { path: 'new-password/:token', element: lazyRoute(<UserPasswordUpdate />) },
      { path: 'reset-password', element: lazyRoute(<PasswordReset />) },
      { path: 'user/activate/:token', element: lazyRoute(<UserActivation />) },
      {
        path: 'account',
        element: lazyRoute(<Account />),
        children: [
          { index: true, element: <AccountOverview /> },
          { path: 'notifications', element: <Notifications /> },
          { path: 'security', element: <Security /> },
        ],
      },
      { path: 'about-us', element: lazyRoute(<AboutUs />) },
      { path: 'contact-sec', element: lazyRoute(<ContactSecurity />) },
      { path: 'contact-us', element: lazyRoute(<ContactUs />) },
      { path: 'data-privacy', element: lazyRoute(<DataPrivacy />) },
      { path: 'gtc', element: lazyRoute(<Gtac />) },
      { path: 'docs/:pageTitle', element: lazyRoute(<Documentation />) },
      { path: 'whats-new', element: lazyRoute(<WhatsNew />) },
      { path: 'support-and-feedback', element: lazyRoute(<GiveUsFeedback />) },
      {
        path: 'products',
        children: [
          {
            path: 'compute',
            children: [
              { index: true, element: lazyRoute(<UserProducts type='compute' />) },
              {
                path: 'free-trial',
                element: protectedLazyRoute('/products/compute/free-trial', Evaluation),
              },
              {
                path: 'order',
                element: protectedLazyRoute('/products/compute/order', ComputeOrder),
              },
              { path: ':productId', element: lazyRoute(<ProductDetails />), loader: productLoader },
              {
                path: ':productId/osprojects/:juiceGroup/:cloudId/:osprojectName',
                element: lazyRoute(<OsProject />),
                loader: osProjectLoader,
              },
            ],
          },
          {
            path: 'caas-on-openstack',
            children: [
              { index: true, element: lazyRoute(<UserProducts type='caas' />) },
              { path: ':productId', element: lazyRoute(<ProductDetails />), loader: productLoader },
              {
                path: ':productId/caas-clusters/:clusterName',
                element: lazyRoute(
                  <CaasClusterDetails
                    clusterData={{
                      cloud_id: '',
                      created: '',
                      device_count: 0,
                      juice_group: '',
                      last_updated: '',
                      name: '',
                      owner: '',
                      type: '',
                      version: '',
                      status: '',
                    }}
                  />
                ),
                loader: caasClusterLoader,
              },
            ],
          },
          {
            path: 'caas-clusters',
            element: protectedLazyRoute('/products', CaasClusters),
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: caasClusterLoader,
          },
          {
            path: 'baremetal',
            children: [
              { index: true, element: lazyRoute(<UserProducts type='baremetal' />) },
              { path: ':productId', element: lazyRoute(<ProductDetails />), loader: productLoader },
            ],
          },
          {
            path: 'safescarf',
            element: lazyRoute(<SafescarfProduct />),
            children: [
              { index: true, element: lazyRoute(<SafescarfOverview />) },
              { path: 'users', element: lazyRoute(<SafescarfUsers />) },
              { path: 'machine-accounts', element: lazyRoute(<SafescarfMachineAccounts />) },
              { path: 'product-types', element: lazyRoute(<SafescarfProductTypes />) },
              { path: 'products', element: lazyRoute(<SafescarfProducts />) },
            ],
          },
          {
            path: 'safescarf',
            children: [
              {
                path: 'product-types/:productTypeId',
                element: lazyRoute(<SafescarfProductTypeDetails />),
              },
              {
                path: 'product-types/:productTypeId/members/:safescarfUserId',
                element: lazyRoute(<ProductTypeMemberDetails />),
              },
              { path: 'products/:productId', element: lazyRoute(<SafescarfProductDetails />) },
              {
                path: 'products/:productId/members/:safescarfUserId',
                element: lazyRoute(<ProductMemberDetails />),
              },
            ],
          },
        ],
      },
      {
        path: 'products/:productId',
        children: [
          { index: true, element: lazyRoute(<ProductDetails />), loader: productLoader },
          {
            path: 'contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: contactLoader,
          },
          {
            path: 'osprojects/:juiceGroup/:cloudId/:osprojectName',
            element: lazyRoute(<OsProject />),
            loader: osProjectLoader,
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: caasClusterLoader,
          },
        ],
      },
      { path: 'products/compute/specification', element: lazyRoute(<ComputeSpecification />) },
      { path: 'products/safescarf/specification', element: lazyRoute(<SafescarfSpecification />) },
      { path: 'products/caas/specification', element: lazyRoute(<CaasSpecification />) },
      {
        path: 'products/monitoring/specification',
        element: lazyRoute(<MonitoringSpecification />),
      },
      { path: 'products/baremetal/specification', element: lazyRoute(<BareMetalSpecification />) },
      {
        path: 'organizations',
        element: (
          <Navigate
            to='/governance/organizations'
            replace
          />
        ),
      },
      {
        path: 'governance',
        element: (
          <Navigate
            to='/governance/organizations'
            replace
          />
        ),
      },
      { path: 'governance/organizations', element: lazyRoute(<GovernanceTabs />) },
      { path: 'governance/users', element: lazyRoute(<GovernanceTabs />) },
      {
        path: 'governance/organizations/:organizationId',
        element: lazyRoute(<OrganizationDetails />),
        loader: organizationLoader,
        children: [
          { index: true, element: lazyRoute(<Overview />) },
          { path: 'users', element: lazyRoute(<Users />) },
          { path: 'business-units', element: lazyRoute(<BusinessUnits />) },
          { path: 'projects', element: lazyRoute(<Projects />) },
          { path: 'products', element: lazyRoute(<UserProducts type='all' />) },
          { path: 'monitoring-dashboards', element: lazyRoute(<Dashboards />) },
        ],
      },
      {
        path: 'organizations/:organizationId',
        element: lazyRoute(<OrganizationDetails />),
        loader: organizationLoader,
        children: [
          { index: true, element: lazyRoute(<Overview />) },
          { path: 'users', element: lazyRoute(<Users />) },
          { path: 'business-units', element: lazyRoute(<BusinessUnits />) },
          { path: 'projects', element: lazyRoute(<Projects />) },
          { path: 'products', element: lazyRoute(<UserProducts type='all' />) },
          { path: 'monitoring-dashboards', element: lazyRoute(<Dashboards />) },
        ],
      },
      { path: 'users/:uuid', element: lazyRoute(<UserDetails />), loader: userLoader },
      {
        path: 'business-units/:businessUnitId',
        element: lazyRoute(<BusinessUnitDetails />),
        loader: businessUnitLoader,
      },
      { path: 'projects/:projectId', element: lazyRoute(<Project />), loader: projectLoader },
      {
        path: 'reports',
        element: lazyRoute(<Reports />),
        children: [
          { path: 'tenants', element: lazyRoute(<TenantsReport />) },
          { path: 'tenants/:cloudId/:juiceGroup/:tenant', element: lazyRoute(<TenantDetails />) },
          {
            path: 'tenants/:cloudId/:juiceGroup/:tenant/contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: contactLoader,
          },
          { path: 'hypervisors', element: lazyRoute(<HypervisorReport />) },
          {
            path: 'hypervisors/:cloudId/:juiceGroup/:virtualMachine',
            element: lazyRoute(<HypervisorDetails />),
          },
          {
            path: 'hypervisors/:cloudId/:juiceGroup/:virtualMachine/contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: contactLoader,
          },
          { path: 'juicegroups', element: lazyRoute(<JuiceGroups />) },
          {
            path: '/reports/juicegroups/contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: contactLoader,
          },
          {
            path: 'caas-clusters',
            element: protectedLazyRoute('/reports', CaasClusters),
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: caasClusterLoader,
          },
        ],
      },
    ],
  },
  { path: '*', element: lazyRoute(<Page404 />) },
];

export const appPath = '/';

function createBaseRouter() {
  return createBrowserRouter(routes, {
    basename: appPath,
  });
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

export function createAppRouter() {
  return sentryCreateBrowserRouter(routes, {
    basename: appPath,
  });
}

export { createBaseRouter };
